import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormControlLabel, Grid, Switch, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addBusDemand, editBusDemand } from '../../../api/endpoints/demands';
import type { BusDemand } from '../../../api/types/demands';
import { DemandFormDialog } from '../components/DemandFormDialog';
import { PhoneField } from '../../../components/PhoneField';

const schema = z.object({
  employeeID: z.coerce.number().min(1, 'Personel ID gerekli'),
  passengerName: z.string().optional(),
  passengerSurname: z.string().optional(),
  passengerNationality: z.string().optional(),
  passengerIdNumber: z.string().optional(),
  passengerMail: z.string().optional(),
  passengerPhone: z.string().optional(),
  from: z.string().min(1, 'Kalkış yeri gerekli'),
  to: z.string().min(1, 'Varış yeri gerekli'),
  depDate: z.string().min(1, 'Gidiş tarihi gerekli'),
  retDate: z.string().optional(),
  depTime: z.string().optional(),
  retTime: z.string().optional(),
  isTicketed: z.boolean().optional(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  employeeID: 0,
  passengerName: '',
  passengerSurname: '',
  passengerNationality: '',
  passengerIdNumber: '',
  passengerMail: '',
  passengerPhone: '',
  from: '',
  to: '',
  depDate: '',
  retDate: '',
  depTime: '',
  retTime: '',
  isTicketed: false,
};

function toFormValues(demand: BusDemand): FormValues {
  return {
    employeeID: demand.employeeID ?? 0,
    passengerName: demand.passengerName ?? '',
    passengerSurname: demand.passengerSurname ?? '',
    passengerNationality: demand.passengerNationality ?? '',
    passengerIdNumber: demand.passengerIdNumber ?? '',
    passengerMail: demand.passengerMail ?? '',
    passengerPhone: demand.passengerPhone ?? '',
    from: demand.from ?? '',
    to: demand.to ?? '',
    depDate: demand.depDate ?? '',
    retDate: demand.retDate ?? '',
    depTime: demand.depTime ?? '',
    retTime: demand.retTime ?? '',
    isTicketed: demand.isTicketed ?? false,
  };
}

/** Create/edit dialog for Otobüs Bileti Talebi. Edit also serves as the status-update
 *  action (the "Biletlendi" switch, wired to EditBusDemand's `isTicketed` field). */
export function BusDemandDialog({
  open,
  demand,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit/status-update mode for this row. */
  demand: BusDemand | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!demand;

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: emptyValues,
  });

  useEffect(() => {
    if (open) reset(demand ? toFormValues(demand) : emptyValues);
  }, [open, demand, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && demand?.id
        ? editBusDemand({ ...values, id: demand.id })
        : addBusDemand(values),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Talep güncellendi.' : 'Talep oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['demands', 'bus'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <DemandFormDialog
      open={open}
      title={isEdit ? 'Otobüs Bileti Talebini Güncelle' : 'Yeni Otobüs Bileti Talebi'}
      onClose={onClose}
      onSubmit={submit}
      submitting={mutation.isPending}
      error={mutation.error}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Personel ID (EmployeeID)"
            error={!!errors.employeeID}
            helperText={errors.employeeID?.message ?? 'Kurumsal çalışanın sayısal ID değeri'}
            {...register('employeeID')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth size="small" label="Uyruk" {...register('passengerNationality')} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth size="small" label="Yolcu Adı" {...register('passengerName')} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth size="small" label="Yolcu Soyadı" {...register('passengerSurname')} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth size="small" label="TC Kimlik No" {...register('passengerIdNumber')} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth size="small" label="E-posta" {...register('passengerMail')} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            control={control}
            name="passengerPhone"
            render={({ field }) => (
              <PhoneField value={field.value} onChange={field.onChange} onBlur={field.onBlur} label="Telefon" />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} />
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Kalkış (From)"
            error={!!errors.from}
            helperText={errors.from?.message}
            {...register('from')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Varış (To)"
            error={!!errors.to}
            helperText={errors.to?.message}
            {...register('to')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Gidiş Tarihi"
            slotProps={{ inputLabel: { shrink: true } }}
            error={!!errors.depDate}
            helperText={errors.depDate?.message}
            {...register('depDate')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Dönüş Tarihi"
            slotProps={{ inputLabel: { shrink: true } }}
            {...register('retDate')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            type="time"
            label="Tercih Edilen Gidiş Saati"
            slotProps={{ inputLabel: { shrink: true } }}
            {...register('depTime')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            type="time"
            label="Tercih Edilen Dönüş Saati"
            slotProps={{ inputLabel: { shrink: true } }}
            {...register('retTime')}
          />
        </Grid>
        {isEdit && (
          <Grid size={{ xs: 12 }}>
            <Controller
              control={control}
              name="isTicketed"
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Biletlendi"
                />
              )}
            />
          </Grid>
        )}
      </Grid>
    </DemandFormDialog>
  );
}
